"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import FilterBar from "@/components/ui/FilterBar";
import DataTable from "@/components/ui/DataTable";
import StatCard from "@/components/ui/StatCard";
import Pagination from "@/components/tables/Pagination";
import Button from "@/components/ui/button/Button";
import SearchableSelect from "@/components/ui/SearchableSelect";
import DatePicker from "@/components/form/date-picker";
import { rmcClient } from "@/lib/rmcClient";
import { useQuery } from "@tanstack/react-query";
import React from "react";

const PAGE_SIZE = 20;

function extractReportRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data?.results)) return data.data.results;
  return [];
}

function readPaginatedListMeta(raw) {
  if (!raw || typeof raw !== "object") {
    return { count: null, hasNext: false, hasPrevious: false, totalPages: null };
  }
  const meta = raw.meta && typeof raw.meta === "object" ? raw.meta : null;
  const tryCount = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    for (const key of ["count", "total", "total_count"]) {
      const n = Number(obj[key]);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    return null;
  };
  const count = tryCount(raw) ?? tryCount(raw.data) ?? tryCount(meta);
  const totalPagesNum = Number(meta?.total_pages);
  const totalPages =
    Number.isFinite(totalPagesNum) && totalPagesNum > 0 ? Math.floor(totalPagesNum) : null;
  return {
    count,
    totalPages,
    hasNext: Boolean(raw.next ?? meta?.next),
    hasPrevious: Boolean(raw.previous ?? meta?.previous),
  };
}

function readListMetaBlock(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const m = raw.meta ?? raw.data?.meta;
  return m && typeof m === "object" ? m : null;
}

function fmtQty(v) {
  return Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

export default function RmcStockReports() {
  const toLocalIsoDate = React.useCallback((dateObj) => {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);

  const isoToday = React.useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);
  const [tab, setTab] = React.useState("movement");
  const [period, setPeriod] = React.useState("daily");
  const [matId, setMatId] = React.useState("");
  const [fromDate, setFromDate] = React.useState(isoToday);
  const [toDate, setToDate] = React.useState(isoToday);
  const [appliedMovement, setAppliedMovement] = React.useState({
    period: "daily",
    matId: "",
    fromDate: isoToday,
    toDate: isoToday,
  });
  const [appliedConsumption, setAppliedConsumption] = React.useState({
    partyId: "",
    gradeIds: [],
    materialId: "",
    fromDate: isoToday,
    toDate: isoToday,
  });
  const [consumptionMode, setConsumptionMode] = React.useState("grade");
  const [consPage, setConsPage] = React.useState(1);
  const [materialConsPage, setMaterialConsPage] = React.useState(1);

  const getPeriodDates = React.useCallback((selectedPeriod) => {
    const today = new Date();
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const start = new Date(end);
    if (selectedPeriod === "daily") {
      return { from: toLocalIsoDate(end), to: toLocalIsoDate(end) };
    }
    if (selectedPeriod === "weekly") {
      start.setDate(end.getDate() - 6);
      return { from: toLocalIsoDate(start), to: toLocalIsoDate(end) };
    }
    if (selectedPeriod === "monthly") {
      start.setDate(1);
      return { from: toLocalIsoDate(start), to: toLocalIsoDate(end) };
    }
    if (selectedPeriod === "yearly") {
      start.setMonth(0, 1);
      return { from: toLocalIsoDate(start), to: toLocalIsoDate(end) };
    }
    return { from: "", to: "" };
  }, [toLocalIsoDate]);

  const periodOptions = React.useMemo(
    () => [
      { id: "daily", name: "Daily" },
      { id: "weekly", name: "Weekly" },
      { id: "monthly", name: "Monthly" },
      { id: "yearly", name: "Yearly" },
      { id: "custom", name: "Custom" },
    ],
    []
  );

  const { data: materials = [] } = useQuery({
    queryKey: ["materials"],
    queryFn: async () => (await rmcClient.listMaterials()).data,
  });
  const [consPartyId, setConsPartyId] = React.useState("");
  const [consMaterialId, setConsMaterialId] = React.useState("");
  const { data: parties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });
  const { data: grades = [] } = useQuery({
    queryKey: ["grades", consPartyId],
    queryFn: async () => (await rmcClient.listGrades({ party_id: consPartyId })).data,
    enabled: !!consPartyId,
  });
  const { data: mov = [] } = useQuery({
    queryKey: ["stockPeriod", appliedMovement],
    queryFn: async () =>
      (
        await rmcClient.getStockPeriodReport({
          period: appliedMovement.period,
          material_id: appliedMovement.matId || undefined,
          date_from: appliedMovement.fromDate || undefined,
          date_to: appliedMovement.toDate || undefined,
        })
      ).data,
    enabled: tab === "movement",
  });
  const [selGrades, setSelGrades] = React.useState([]);

  const gradeConsumptionQueryParams = React.useMemo(
    () => ({
      party_id: appliedConsumption.partyId || undefined,
      grade_ids: appliedConsumption.gradeIds.length
        ? appliedConsumption.gradeIds.join(",")
        : undefined,
      date_from: appliedConsumption.fromDate || undefined,
      date_to: appliedConsumption.toDate || undefined,
      page: consPage,
      page_size: PAGE_SIZE,
    }),
    [appliedConsumption, consPage]
  );

  const gradeConsumptionTotalsParams = React.useMemo(
    () => ({
      party_id: appliedConsumption.partyId || undefined,
      grade_ids: appliedConsumption.gradeIds.length
        ? appliedConsumption.gradeIds.join(",")
        : undefined,
      date_from: appliedConsumption.fromDate || undefined,
      date_to: appliedConsumption.toDate || undefined,
      pagination: false,
    }),
    [appliedConsumption]
  );

  const materialConsumptionQueryParams = React.useMemo(
    () => ({
      date_from: appliedConsumption.fromDate || undefined,
      date_to: appliedConsumption.toDate || undefined,
      material_id: appliedConsumption.materialId || undefined,
      page: materialConsPage,
      page_size: PAGE_SIZE,
    }),
    [appliedConsumption, materialConsPage]
  );

  const materialConsumptionTotalsParams = React.useMemo(
    () => ({
      date_from: appliedConsumption.fromDate || undefined,
      date_to: appliedConsumption.toDate || undefined,
      material_id: appliedConsumption.materialId || undefined,
      pagination: false,
    }),
    [appliedConsumption]
  );

  const { data: consData, isFetching: isConsFetching } = useQuery({
    queryKey: ["consumption", gradeConsumptionQueryParams],
    queryFn: async () => (await rmcClient.getConsumptionReport(gradeConsumptionQueryParams)).data,
    enabled: tab === "consumption" && consumptionMode === "grade",
  });

  const { data: consTotalsData } = useQuery({
    queryKey: ["consumptionTotals", gradeConsumptionTotalsParams],
    queryFn: async () =>
      (await rmcClient.getConsumptionReport(gradeConsumptionTotalsParams)).data,
    enabled: tab === "consumption" && consumptionMode === "grade",
  });

  const { data: consByMaterialData, isFetching: isMaterialConsFetching } = useQuery({
    queryKey: ["consumptionMaterial", materialConsumptionQueryParams],
    queryFn: async () =>
      (await rmcClient.getMaterialConsumptionReport(materialConsumptionQueryParams)).data,
    enabled: tab === "consumption" && consumptionMode === "material",
  });

  const { data: consByMaterialTotalsData } = useQuery({
    queryKey: ["consumptionMaterialTotals", materialConsumptionTotalsParams],
    queryFn: async () =>
      (await rmcClient.getMaterialConsumptionReport(materialConsumptionTotalsParams)).data,
    enabled: tab === "consumption" && consumptionMode === "material",
  });

  const applyMovement = () => {
    const range = period === "custom" ? { from: fromDate, to: toDate } : getPeriodDates(period);
    setAppliedMovement({
      period: period === "custom" ? undefined : period,
      matId: matId || "",
      fromDate: range.from,
      toDate: range.to,
    });
  };

  const applyConsumption = () => {
    setAppliedConsumption({
      partyId: consPartyId || "",
      gradeIds: selGrades,
      materialId: consMaterialId || "",
      fromDate,
      toDate,
    });
    setConsPage(1);
    setMaterialConsPage(1);
  };

  const consPaginationMeta = React.useMemo(() => readPaginatedListMeta(consData), [consData]);
  const materialConsPaginationMeta = React.useMemo(
    () => readPaginatedListMeta(consByMaterialData),
    [consByMaterialData]
  );
  const consListMeta = React.useMemo(() => readListMetaBlock(consData), [consData]);
  const materialConsListMeta = React.useMemo(
    () => readListMetaBlock(consByMaterialData),
    [consByMaterialData]
  );

  const consTotalPages = React.useMemo(() => {
    if (consPaginationMeta.totalPages != null) {
      return Math.max(1, consPaginationMeta.totalPages);
    }
    if (consPaginationMeta.count != null) {
      return Math.max(1, Math.ceil(consPaginationMeta.count / PAGE_SIZE));
    }
    if (consPaginationMeta.hasNext) {
      return Math.max(consPage + 1, 1);
    }
    return 1;
  }, [consPaginationMeta, consPage]);

  const materialConsTotalPages = React.useMemo(() => {
    if (materialConsPaginationMeta.totalPages != null) {
      return Math.max(1, materialConsPaginationMeta.totalPages);
    }
    if (materialConsPaginationMeta.count != null) {
      return Math.max(1, Math.ceil(materialConsPaginationMeta.count / PAGE_SIZE));
    }
    if (materialConsPaginationMeta.hasNext) {
      return Math.max(materialConsPage + 1, 1);
    }
    return 1;
  }, [materialConsPaginationMeta, materialConsPage]);

  const consCurrentPage = Math.min(consPage, consTotalPages);
  const materialConsCurrentPage = Math.min(materialConsPage, materialConsTotalPages);
  const showConsPagination = consTotalPages > 1 || consPaginationMeta.hasNext;
  const showMaterialConsPagination =
    materialConsTotalPages > 1 || materialConsPaginationMeta.hasNext;

  const movementRows = React.useMemo(
    () =>
      (Array.isArray(mov) ? mov : []).map((row, idx) => {
        const num = (value) => {
          const n = Number(value);
          return Number.isFinite(n) ? n.toLocaleString("en-IN", { maximumFractionDigits: 3 }) : "—";
        };
        return {
          id: row.id ?? `${row.material ?? "material"}-${idx}`,
          material: row.material ?? "—",
          uom: row.uom ?? "—",
          opening: num(row.opening),
          total_in: num(row.in ?? row.total_in),
          total_out: num(row.out ?? row.total_out),
          closing: num(row.closing),
        };
      }),
    [mov]
  );

  const mapGradeConsumptionRows = React.useCallback((rows) => {
    return (rows || []).map((row, idx) => {
      const sold = Number(row.qty_sold_cum ?? row.qty_sold ?? 0);
      const consumptionItems = Array.isArray(row.consumption) ? row.consumption : [];
      return {
        id: row.id ?? `${row.grade ?? "grade"}-${idx}`,
        grade: row.grade ?? "—",
        qty_sold_cum: Number.isFinite(sold)
          ? sold.toLocaleString("en-IN", { maximumFractionDigits: 3 })
          : "—",
        qty_sold_num: Number.isFinite(sold) ? sold : 0,
        consumptionItems: consumptionItems.map((item) => {
          const qty = Number(item.qty_consumed);
          return {
            material: item.material ?? "Material",
            qty: Number.isFinite(qty)
              ? qty.toLocaleString("en-IN", { maximumFractionDigits: 3 })
              : "—",
            qty_num: Number.isFinite(qty) ? qty : 0,
            uom: item.uom ?? "",
          };
        }),
      };
    });
  }, []);

  const consumptionRows = React.useMemo(
    () => mapGradeConsumptionRows(extractReportRows(consData)),
    [consData, mapGradeConsumptionRows]
  );

  const flattenMaterialConsumption = React.useCallback((rawRows) => {
    const out = [];
    (rawRows || []).forEach((row, idx) => {
      const materialName = row.material_name ?? row.material ?? "—";
      const uom = row.uom ?? "—";
      const gradeRows = Array.isArray(row.consumption_by_grade) ? row.consumption_by_grade : [];
      if (!gradeRows.length) {
        out.push({
          id: `${row.material_id ?? idx}-nograde`,
          grade_name: "—",
          material: materialName,
          qty_consumed: "—",
          qty_consumed_num: 0,
          uom,
        });
        return;
      }
      gradeRows.forEach((g, gIdx) => {
        const gQty = Number(g.qty_consumed ?? 0);
        out.push({
          id: `${g.grade_id ?? gIdx}-${row.material_id ?? idx}`,
          grade_name: g.grade_name ?? `Grade #${gIdx + 1}`,
          material: materialName,
          qty_consumed: Number.isFinite(gQty)
            ? gQty.toLocaleString("en-IN", { maximumFractionDigits: 3 })
            : "—",
          qty_consumed_num: Number.isFinite(gQty) ? gQty : 0,
          uom,
        });
      });
    });
    return out.sort((a, b) => String(a.grade_name).localeCompare(String(b.grade_name)));
  }, []);

  const materialConsumptionRows = React.useMemo(
    () => flattenMaterialConsumption(extractReportRows(consByMaterialData)),
    [consByMaterialData, flattenMaterialConsumption]
  );

  const gradeConsumptionTotals = React.useMemo(() => {
    const meta = readListMetaBlock(consTotalsData) ?? consListMeta;
    const metaSold = Number(meta?.total_qty_sold_cum ?? meta?.total_qty_sold ?? meta?.total_qty_cum);
    const metaConsumed = Number(meta?.total_qty_consumed ?? meta?.total_consumed);
    const allRows = extractReportRows(consTotalsData);
    const computedSold = allRows.reduce(
      (sum, row) => sum + (Number(row.qty_sold_cum ?? row.qty_sold ?? 0) || 0),
      0
    );
    const computedConsumed = allRows.reduce((sum, row) => {
      const items = Array.isArray(row.consumption) ? row.consumption : [];
      return sum + items.reduce((s, item) => s + (Number(item.qty_consumed) || 0), 0);
    }, 0);
    return {
      totalQtySold:
        Number.isFinite(metaSold) && metaSold >= 0 ? metaSold : computedSold,
      totalMaterialConsumed:
        Number.isFinite(metaConsumed) && metaConsumed >= 0 ? metaConsumed : computedConsumed,
      grades: allRows.length,
    };
  }, [consTotalsData, consListMeta]);

  const materialConsumptionTotals = React.useMemo(() => {
    const meta = readListMetaBlock(consByMaterialTotalsData) ?? materialConsListMeta;
    const metaConsumed = Number(meta?.total_qty_consumed ?? meta?.total_consumed);
    const allRows = flattenMaterialConsumption(extractReportRows(consByMaterialTotalsData));
    const computedConsumed = allRows.reduce((sum, row) => sum + (row.qty_consumed_num || 0), 0);
    return {
      totalQtyConsumed:
        Number.isFinite(metaConsumed) && metaConsumed >= 0 ? metaConsumed : computedConsumed,
      entries: allRows.length,
    };
  }, [consByMaterialTotalsData, materialConsListMeta, flattenMaterialConsumption]);

  React.useEffect(() => {
    setSelGrades([]);
  }, [consPartyId]);

  React.useEffect(() => {
    setConsPage(1);
    setMaterialConsPage(1);
  }, [consumptionMode]);

  React.useEffect(() => {
    if (period === "custom") return;
    const range = getPeriodDates(period);
    setFromDate(range.from);
    setToDate(range.to);
  }, [period, getPeriodDates]);

  const effectiveFromDate = period === "custom" ? fromDate : getPeriodDates(period).from;
  const effectiveToDate = period === "custom" ? toDate : getPeriodDates(period).to;

  return (
    <div className="space-y-6">
      <PageBreadCrumb pageTitle="Stock Reports" />
      <div className="flex gap-2 text-xs">
        <Button
          size="sm"
          variant={tab === "movement" ? "primary" : "outline"}
          onClick={() => setTab("movement")}
        >
          Movement
        </Button>
        <Button
          size="sm"
          variant={tab === "consumption" ? "primary" : "outline"}
          onClick={() => setTab("consumption")}
        >
          Consumptions
        </Button>
      </div>
      {tab === "movement" && (
        <>
          <FilterBar>
            <div>
              <p className="text-[10px] text-gray-500 mb-1">Period</p>
              <div className="min-w-[170px]">
                <SearchableSelect
                  value={period}
                  onChange={(v) => setPeriod(v || "daily")}
                  options={periodOptions}
                  placeholder="Select period…"
                />
              </div>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 mb-1">Material</p>
              <div className="min-w-[220px]">
                <SearchableSelect
                  value={matId}
                  onChange={(v) => setMatId(v || "")}
                  options={[{ id: "", name: "All" }, ...materials]}
                  placeholder="All materials…"
                />
              </div>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 mb-1">From</p>
              <div className="min-w-[170px]">
                <DatePicker
                  id="stock-movement-from"
                  defaultDate={effectiveFromDate}
                  placeholder="From date"
                  disabled={period !== "custom"}
                  onChange={(selectedDates, dateStr) => setFromDate(dateStr || "")}
                />
              </div>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 mb-1">To</p>
              <div className="min-w-[170px]">
                <DatePicker
                  id="stock-movement-to"
                  defaultDate={effectiveToDate}
                  placeholder="To date"
                  disabled={period !== "custom"}
                  onChange={(selectedDates, dateStr) => setToDate(dateStr || "")}
                />
              </div>
            </div>
            <Button size="sm" className="self-end" onClick={applyMovement}>
              Apply
            </Button>
          </FilterBar>
          <DataTable
            columns={[
              { key: "material", header: "Material" },
              { key: "uom", header: "UOM" },
              { key: "opening", header: "Opening" },
              { key: "total_in", header: "Total IN" },
              { key: "total_out", header: "Total OUT" },
              { key: "closing", header: "Closing" },
            ]}
            data={movementRows}
            isLoading={false}
          />
        </>
      )}
      {tab === "consumption" && (
        <>
          <div className="flex gap-2 text-xs">
            <Button
              size="sm"
              variant={consumptionMode === "grade" ? "primary" : "outline"}
              onClick={() => setConsumptionMode("grade")}
            >
              Consumption by grade
            </Button>
            <Button
              size="sm"
              variant={consumptionMode === "material" ? "primary" : "outline"}
              onClick={() => setConsumptionMode("material")}
            >
              Consumption by material
            </Button>
          </div>
          <FilterBar>
            <div>
              <p className="text-[10px] text-gray-500 mb-1">From</p>
              <div className="min-w-[170px]">
                <DatePicker
                  id="stock-consumption-from"
                  defaultDate={fromDate}
                  placeholder="From date"
                  onChange={(selectedDates, dateStr) => setFromDate(dateStr || "")}
                />
              </div>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 mb-1">To</p>
              <div className="min-w-[170px]">
                <DatePicker
                  id="stock-consumption-to"
                  defaultDate={toDate}
                  placeholder="To date"
                  onChange={(selectedDates, dateStr) => setToDate(dateStr || "")}
                />
              </div>
            </div>
            {consumptionMode === "grade" && (
              <>
                <div>
                  <p className="text-[10px] text-gray-500 mb-1">Party</p>
                  <div className="min-w-[220px]">
                    <SearchableSelect
                      value={consPartyId}
                      onChange={(v) => setConsPartyId(v || "")}
                      options={parties}
                      getSubLabel={(p) => p.gst_number}
                      placeholder="Select party…"
                    />
                  </div>
                </div>
                <div className="min-w-[260px]">
                  <p className="text-[10px] text-gray-500 mb-1">Grades</p>
                  {!consPartyId ? (
                    <div className="rounded-lg border border-gray-800 bg-gray-950/50 p-2 text-xs text-gray-500">
                      Select a party to load grades.
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2 rounded-lg border border-gray-800 bg-gray-950/50 p-2">
                      {grades.map((g) => {
                        const active = selGrades.includes(g.id);
                        return (
                          <button
                            key={g.id}
                            type="button"
                            onClick={() =>
                              setSelGrades((prev) =>
                                prev.includes(g.id) ? prev.filter((x) => x !== g.id) : [...prev, g.id]
                              )
                            }
                            className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
                              active
                                ? "menu-dropdown-badge-active border-white/70 text-white"
                                : "border-gray-700 text-gray-300 hover:border-gray-500"
                            }`}
                          >
                            {g.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
            {consumptionMode === "material" && (
              <div>
                <p className="text-[10px] text-gray-500 mb-1">Material</p>
                <div className="min-w-[220px]">
                  <SearchableSelect
                    value={consMaterialId}
                    onChange={(v) => setConsMaterialId(v || "")}
                    options={[{ id: "", name: "All materials" }, ...materials]}
                    placeholder="All materials…"
                  />
                </div>
              </div>
            )}
            <Button size="sm" className="self-end" onClick={applyConsumption}>
              Apply
            </Button>
          </FilterBar>
          {consumptionMode === "grade" ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <StatCard
                  label="Total Qty Sold (CUM)"
                  value={fmtQty(gradeConsumptionTotals.totalQtySold)}
                  hint="Across all filtered grades"
                />
                <StatCard
                  label="Total Raw Material Consumed"
                  value={fmtQty(gradeConsumptionTotals.totalMaterialConsumed)}
                  hint="Across all filtered grades"
                />
                <StatCard label="Grades" value={gradeConsumptionTotals.grades} />
              </div>
              <DataTable
              columns={[
                { key: "grade", header: "Grade" },
                { key: "qty_sold_cum", header: "Qty Sold (CUM)" },
                {
                  key: "mat",
                  header: "Raw material consumption",
                  render: (r) => {
                    if (!r.consumptionItems?.length) {
                      return <span className="text-gray-400">No consumption</span>;
                    }
                    return (
                      <div className="overflow-hidden rounded-md border border-gray-800">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-900/80 text-gray-400">
                              <th className="px-2 py-1.5 text-left font-medium">Material</th>
                              <th className="px-2 py-1.5 text-right font-medium">Qty</th>
                              <th className="px-2 py-1.5 text-left font-medium">UOM</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.consumptionItems.map((item, i) => (
                              <tr
                                key={`${item.material}-${i}`}
                                className="border-t border-gray-800/80 text-gray-200"
                              >
                                <td className="px-2 py-1.5 font-medium">{item.material}</td>
                                <td className="px-2 py-1.5 text-right">{item.qty}</td>
                                <td className="px-2 py-1.5 text-gray-400">{item.uom || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  },
                },
              ]}
              data={consumptionRows}
              isLoading={isConsFetching}
              footer={[
                <td key="label" className="px-4 py-3 font-semibold text-gray-900 dark:text-white">
                  Total (filtered)
                </td>,
                <td
                  key="sold"
                  className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white"
                >
                  {fmtQty(gradeConsumptionTotals.totalQtySold)}
                </td>,
                <td
                  key="consumed"
                  className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white"
                >
                  {fmtQty(gradeConsumptionTotals.totalMaterialConsumed)}
                </td>,
              ]}
            />
              {showConsPagination && (
                <div className="flex justify-end">
                  <Pagination
                    currentPage={consCurrentPage}
                    totalPages={consTotalPages}
                    onPageChange={(p) => setConsPage(Math.max(1, Math.min(consTotalPages, p)))}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <StatCard
                  label="Total Qty Consumed"
                  value={fmtQty(materialConsumptionTotals.totalQtyConsumed)}
                  hint="Across all filtered grade × material rows"
                />
                <StatCard label="Entries" value={materialConsumptionTotals.entries} />
              </div>
              <DataTable
              columns={[
                { key: "grade_name", header: "Grade" },
                { key: "material", header: "Material" },
                {
                  key: "qty_consumed",
                  header: "Qty Consumed",
                  cellClassName: "text-right",
                },
                { key: "uom", header: "UOM" },
              ]}
              data={materialConsumptionRows}
              isLoading={isMaterialConsFetching}
              footer={[
                <td
                  key="label"
                  colSpan={2}
                  className="px-4 py-3 font-semibold text-gray-900 dark:text-white"
                >
                  Total (filtered)
                </td>,
                <td
                  key="qty"
                  className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white"
                >
                  {fmtQty(materialConsumptionTotals.totalQtyConsumed)}
                </td>,
                <td key="uom" className="px-4 py-3 text-gray-500">
                  —
                </td>,
              ]}
            />
              {showMaterialConsPagination && (
                <div className="flex justify-end">
                  <Pagination
                    currentPage={materialConsCurrentPage}
                    totalPages={materialConsTotalPages}
                    onPageChange={(p) =>
                      setMaterialConsPage(Math.max(1, Math.min(materialConsTotalPages, p)))
                    }
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
